import React, { Fragment, useEffect, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Box from "@material-ui/core/Box";

const useStyles = makeStyles((theme) => ({
  videoContainer: {
    overflow: "hidden",
    paddingBottom: "56.25%",
    position: "relative",
    height: 0,
  },
  video: {
    left: 0,
    top: 0,
    height: "100%",
    width: "100%",
    position: "absolute",
  }
}));

function Eyepiece1Resource() {
  const classes = useStyles();

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Typography variant="h5" gutterBottom>
          Eyepiece 1: Simple Surveys with Powerful Conclusions
        </Typography>
        <Typography variant="body1" gutterBottom>
          A key challenge involved in deciding when, and how, to use information
          technologies in education is that the technologies themselves do not
          uniquely determine outcomes. Two teachers may be using digital movie
          editing software in their classes to roughly the same extent, and
          doing an equally good job of handling technical challenges, student
          time involved, etc. - but the teacher that uses it as a direct
          replacement for traditional presentations will see very little impact
          upon student outcomes, while the teacher that uses it to have students
          engage in new forms of conceptual exploration and peer discussion will
          see much more noticeable improvements. Dr. Ruben Puentedura’s SAMR
          model addresses this challenge directly, by merging changes in
          technology and changes in teaching practice into a set of four levels
          with progressively greater impact upon student outcomes:
        </Typography>
        <Box m={5}>
          <img width="100%" src="assets/Eyepiece1/samr.jpeg" />
        </Box>
        <Typography variant="body1" gutterBottom>
          The model can be used successfully for selecting applications of
          technology in education, steering the design of their implementation,
          and predicting, evaluating, and improving outcomes.
        </Typography>
        <Typography variant="body1" gutterBottom>
          A question still remains, though: direct classroom observation and
          teacher interviews will help establish the SAMR level of toolset use
          in actual practice - but is it possible to get at some of this
          information via simple surveys? This first eyepiece answers the
          question in the affirmative, and shows how exploring interactions
          between survey questions, rather than just looking at averages, can
          yield much of the necessary SAMR information.
        </Typography>
        <Typography variant="body1" gutterBottom>
          The eyepiece doesn’t stop there, though: the same approach can help
          identify a key component of successful use of technology in education:
          the development of shared practices, across subject areas within a
          given grade level, and across grade levels within a given school.
          These practices are necessary to the sustainable implementation of
          technology within an institution - when they are absent, technology
          projects tend to progress in fits and starts, if at all.
        </Typography>
        <Typography variant="body1" gutterBottom>
          Finally, a simple visualization added on to the basic analysis allows
          this eyepiece to help determine the existence and structure of
          implicit affinity networks among teachers. The connections defining
          these networks are given by similarities of attitudes towards learning
          and technologies, and are central to addressing topics as diverse as
          the successful adoption of innovations, teacher peer support, and the
          implementation of scalable professional development.
        </Typography>
        <Box m={5}>
          <img width="100%" src="assets/Eyepiece1/network.jpeg" />
        </Box>
        <Box mt={3} mb={5}>
          <Typography variant="h6" gutterBottom>
            Bio
          </Typography>
          <Typography variant="body1" gutterBottom>
            Dr. Ruben Puentedura is the Founder and President of Hippasus, a
            consulting firm based in Western Massachusetts, focusing on
            transformative applications of information technologies to
            education. He has implemented these approaches for over thirty years
            at a range of K-20 educational institutions, as well as health and
            arts organizations.
          </Typography>
          <Typography variant="body1" gutterBottom>
            He is the creator of the SAMR model for selecting, using, and
            evaluating technology in education, which currently guides the work
            of multiple learning projects worldwide. He is also the author of
            The EdTech Quintet, a categorization of the core technology toolset
            required for education derived from the Horizon Report. Both models
            are components of his evolving toolkit for Black Swan and
            antifragile thinking, part of an ongoing project that he has been
            developing for the past decade. As an Innovator in Residence at
            ASU’s ShapingEDU Project, he is currently directing a year-long
            project on Black Swan Thinking, comprising both research and design
            workshops.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Other facets of his work explore new directions in mobile computing,
            digital storytelling, learning analytics, and educational gaming,
            focusing on applications in areas where they have not been
            traditionally employed.
          </Typography>
        </Box>
        <Typography variant="h5" gutterBottom>
          Other Resources
        </Typography>
        <Typography variant="h6" gutterBottom>
          General Research
        </Typography>
        <Link
          href="https://itunes.apple.com/WebObjects/MZStore.woa/wa/viewBook?id=1088992950"
          variant="subtitle1"
          target="_blank"
        >
          Research for Educators Book (Apple Education)
        </Link>
        <Typography variant="body1" gutterBottom>
          While there are many books that cover research in schools, one
          specifically targeted to the needs of educators who are newcomers to
          the field, with contributions from Drs. Puentedura and Bebell.
          Chapters 3 and 7 in Apple Education’s Research for Educators are
          relevant to further developing this first eyepiece
        </Typography>
        <Link
          href="https://www.worldcat.org/title/good-research-guide-for-small-scale-social-research-projects/oclc/999541058"
          target="_blank"
          variant="subtitle1"
        >
          The Good Research Guide
        </Link>
        <Typography variant="body1" gutterBottom>
          For Small-Scale Social Research Projects, this guide is particularly
          well-suited to the needs of new school researchers taking a deeper
          look at the field. It includes multiple useful checklists, and covers
          an astonishingly wide range of topics and possibilities
        </Typography>
        <Typography variant="h6" gutterBottom>
          SAMR and the EdTech Quintet
        </Typography>
        <Link
          href=" https://youtu.be/rMazGEAiZ9c"
          variant="subtitle1"
          target="_blank"
        >
          Technology in Education: A Brief Introduction
        </Link>
        <Typography variant="body1" gutterBottom>
          For an overview of both models in one location, a video by Dr.
          Puentedura covering the basics
        </Typography>

        <Typography variant="body1" gutterBottom>
          A series of videos codeveloped with Common Sense Education, and is
          available on YouTube:
        </Typography>
        <ul>
          <li>
            <Link
              href="https://youtu.be/9b5yvgKQdqE"
              variant="subtitle1"
              target="_blank"
            >
              What is the SAMR Model?
            </Link>
          </li>
          <li>
            <Link
              href="https://youtu.be/ZQTx2UQQvbU"
              variant="subtitle1"
              target="_blank"
            >
              How to Apply the SAMR Model
            </Link>
          </li>
          <li>
            <Link
              href="https://youtu.be/SWU0Dzz6gs0"
              variant="subtitle1"
              target="_blank"
            >
              The Impact of the SAMR Model
            </Link>
          </li>
        </ul>

        <Typography variant="body1" gutterBottom>
          For those curious about the EdTech Quintet, this presentation covers
          the research basics:
        </Typography>
        <ul>
          <li>
            <Link
              href="https://youtu.be/NemBarqD6qA"
              variant="subtitle1"
              target="_blank"
            >
              The NMC Perspective Series: Ideas that Matter
            </Link>
          </li>
        </ul>

        <Typography variant="body1" gutterBottom>
          Finally, for those wishing to dig a little deeper, a conversation
          between Dr. Bebell and Dr. Puentedura has more of the inside story on
          this research:
        </Typography>
        <ul>
          <li>
            <Link
              href="https://youtu.be/L9h9ePoXqS8"
              variant="subtitle1"
              target="_blank"
            >
              Demystifying SAMR
            </Link>
          </li>
        </ul>

        <Typography variant="h6" gutterBottom>
          Social Network Analysis
        </Typography>
        <Typography variant="body1" gutterBottom>
          The very small taste of social network an3alysis included in this
          first eyepiece is powerful, but fully self-contained. That said, for
          those wishing to explore in greater detail the uses and relevance of
          this toolset to antifragile thinking in education,&nbsp;
          <Link href="https://shapingedu.asu.edu/blog/black-swan-thinking-project-session-3-who-framed-narrative-cock-robin">
            a blog post by Dr. Puentedura, encompassing a video presentation and
            multiple tools and reference materials
          </Link>
          .
        </Typography>
      </Grid>
      <Grid item xs={12} md={5}>
        <Box className={classes.videoContainer}>
          <iframe
            className={classes.video}
            src="https://www.youtube.com/embed/ioo41-Pa-BA"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </Box>
      </Grid>
    </Grid>
  );
}

export default Eyepiece1Resource;
